import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MenuCategory } from './types';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';

interface MenuCategoryItemProps {
  category: MenuCategory;
  index: number;
  compactSidebar: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onNavigate: (path: string) => void;
  isActive: (path: string) => boolean;
}

const MenuCategoryItem = ({ 
  category, 
  index, 
  compactSidebar, 
  onNavigate, 
  isActive 
}: MenuCategoryItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useLanguage();

  const handleToggle = () => {
    if (!compactSidebar) setIsExpanded(!isExpanded);
  };

  const handleItemClick = (path: string) => {
    onNavigate(path);
    if (!compactSidebar && !isExpanded) setIsExpanded(true);
  };

  const hasActiveItem = category.items.some(item => isActive(item.path));
  const shouldShowExpanded = !compactSidebar && (isExpanded || hasActiveItem);

  const getCategoryName = () => category.nameKey ? t(category.nameKey) : category.name;
  const getItemName = (item: typeof category.items[0]) => item.nameKey ? t(item.nameKey) : item.name;

  return (
    <div className="mb-2">
      <Button
        variant="ghost"
        className="w-full justify-between p-0 h-auto font-medium text-left transition-colors duration-200 rounded-xl overflow-hidden"
        style={{
          background: hasActiveItem ? 'rgba(124,58,237,0.07)' : 'transparent',
          borderLeft: hasActiveItem ? '3px solid #7C3AED' : '3px solid transparent',
        }}
        onClick={handleToggle}
      >
        <div
          className="flex items-center w-full px-3 py-2.5 gap-2.5"
          style={{ color: hasActiveItem ? '#7C3AED' : '#475569' }}
        >
          <category.icon
            className="h-4 w-4 flex-shrink-0"
            style={{ color: hasActiveItem ? '#7C3AED' : '#94A3B8' }}
          />
          {!compactSidebar && (
            <span
              className="truncate flex-1"
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontSize: '0.82rem',
                fontWeight: hasActiveItem ? 700 : 500,
                color: hasActiveItem ? '#7C3AED' : '#475569',
              }}
            >
              {getCategoryName()}
            </span>
          )}
          {!compactSidebar && (
            <div className="flex-shrink-0" style={{ color: '#94A3B8' }}>
              {shouldShowExpanded ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
            </div>
          )}
        </div>
      </Button>

      {shouldShowExpanded && (
        <div className="ml-3 mt-0.5 space-y-0.5 pl-3" style={{ borderLeft: '1.5px solid #E2E8F0' }}>
          {category.items.map((item) => (
            <Button
              key={item.path}
              variant="ghost"
              className="w-full justify-start p-0 h-auto text-sm transition-all duration-200 rounded-lg overflow-hidden"
              style={{
                background: isActive(item.path) ? 'rgba(124,58,237,0.07)' : 'transparent',
                borderLeft: isActive(item.path) ? '2.5px solid #7C3AED' : '2.5px solid transparent',
              }}
              onClick={() => handleItemClick(item.path)}
            >
              <div
                className="flex items-center gap-2 w-full px-3 py-2"
                style={{ color: isActive(item.path) ? '#7C3AED' : '#475569' }}
              >
                <item.icon
                  className="h-3.5 w-3.5 flex-shrink-0"
                  style={{ color: isActive(item.path) ? '#7C3AED' : '#94A3B8' }}
                />
                <span
                  className="truncate"
                  style={{
                    fontFamily: 'Manrope, sans-serif',
                    fontSize: '0.8rem',
                    fontWeight: isActive(item.path) ? 700 : 400,
                    color: isActive(item.path) ? '#7C3AED' : '#475569',
                  }}
                >
                  {getItemName(item)}
                </span>
              </div>
            </Button>
          ))}
        </div>
      )}

      {compactSidebar && (
        <div className="absolute left-16 bg-popover text-popover-foreground text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
          {getCategoryName()}
        </div>
      )}
    </div>
  );
};

export default MenuCategoryItem;
